/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2022-09-26 11:39:10
 *
 *******************************************************/
// @flow
import React, { Component } from 'react';
import _ from 'lodash';
import { connect } from 'react-redux';
import { withI18n } from '@shopify/react-i18n';
import { Page, Layout, Card, Pagination, Button } from '@shopify/polaris';
import HowToUse from 'src/components/views/recommendation/manual-recommendation/management/how-to-use';
import Search from 'src/components/views/recommendation/manual-recommendation/management/search';
import ManualRecommendationType from './manual-recommendation-type';
import GrantPermissionsNotif from 'src/components/views/recommendation/grant-permissions-notif';
import ExceededPlanNotification from 'src/components/containers/common/exceeded-plan-notifications';
import InstallationRemindStatusBanner from 'src/components/containers/common/installation-remind-status-banner';
import MainMenu from 'src/components/containers/main-menu';
import * as recommendationAction from 'src/stores/actions/recommendation-actions';

import { URL } from 'src/constants/links';
import { routes } from 'src/routes';
import history from 'src/routes/history';
import { prettyText } from 'src/helpers';
import { getShop, buildUpdateAppPermissionURL } from 'src/helpers';
import { hasPermissionRecommendation } from 'src/helpers/permission-helper';
import {
  MANUAL_RECOMMENDATION_TAB_TYPE,
  NUMBER_RECOMMENDATION_PER_PAGE,
  HAND_PICKED_PRODUCT_TAB,
  FREQUENTLY_BOUGHT_TOGETHER_TAB,
  RELATED_ITEMS_TAB,
  SEARCH_RESULT_MANUAL_RECOMMENDATION_TAB
} from 'src/constants/recommendation';

import type { ComposedProps, State } from './index.type';
import TitleHelp from 'src/components/views/common/title-help';

class ManualRecommendationManagement extends Component<ComposedProps, State> {
  state = {
    inputSearchText: '',
    selectedTab: 0,

    activePermissionNotif: false,
    permissionURL: '',

    limit: NUMBER_RECOMMENDATION_PER_PAGE,
    pageHandPicked: 1,
    totalPageHandPicked: 0
  };

  componentDidMount() {
    // Show Grant permission notification
    this.showGrantPermissionsModalNotif();

    // Get manual recommendation hand-picked-product
    this.fetchManualRecommendation();
  }

  componentWillReceiveProps(nextProps) {
    const { limit } = this.state;
    const { manualHandPickedTotal } = this.props;
    const { manualHandPickedTotal: nextManualHandPickedTotal } = nextProps;
    if (manualHandPickedTotal !== nextManualHandPickedTotal) {
      const totalPage = Math.ceil(nextManualHandPickedTotal / limit);
      this.setState({
        totalPageHandPicked: totalPage
      });
    }
  }

  render() {
    const { i18n, host } = this.props;
    const { inputSearchText, selectedTab, activePermissionNotif, permissionURL } = this.state;

    const { frequentlyBoughtTogetherTabIndex, relatedItemTabIndex } = this.getTabIndex();

    const isDisablePrimaryAction =
      (!_.isEmpty(inputSearchText) && selectedTab === SEARCH_RESULT_MANUAL_RECOMMENDATION_TAB) ||
      selectedTab === frequentlyBoughtTogetherTabIndex || // TODO: Remove after implement
      selectedTab === relatedItemTabIndex; // TODO: Remove after implement

    // TODO: Show pagination only in Hand-picked products tab -> change condition here later after opening the rest tabs
    const isShowPagination =
      (!inputSearchText &&
        ![frequentlyBoughtTogetherTabIndex, relatedItemTabIndex].includes(selectedTab)) ||
      (inputSearchText &&
        ![
          frequentlyBoughtTogetherTabIndex,
          relatedItemTabIndex,
          SEARCH_RESULT_MANUAL_RECOMMENDATION_TAB
        ].includes(selectedTab));

    return (
      <div className="BC-Page BC-Page__ManualRecommendationManagement">
        <MainMenu menuId="menu-recommendation" />
        <Page
          title={i18n.translate('AdminHTML.Recommendation.ManualRecommendation.Dashboard.title')}
          primaryAction={
            <div className="BC-Page BC-Page__Button">
              <Button
                primary
                disabled={isDisablePrimaryAction}
                onClick={() => {
                  history.push(routes.manualRecommendation.new());
                }}>
                {i18n.translate(
                  'AdminHTML.Recommendation.ManualRecommendation.Common.button_AddNew'
                )}
              </Button>
            </div>
          }>
          <TitleHelp
            i18n={i18n}
            hrefUserManual={URL['Link_V2_Recommendation_ManualSetting_ManualGuide']}
            hrefBestPractice={URL['Link_V2_Recommendation_ManualSetting_BestPractice']}
          />
          <Layout>
            <Layout.Section>
              <div className="BC-Container__Notifications">
                {/* Installation reminder status banner */}
                <InstallationRemindStatusBanner />

                {/* How To Use */}
                <HowToUse host={host} />

                {/* Sticky notification for exceeded plan */}
                <ExceededPlanNotification />
              </div>
            </Layout.Section>

            {/* Update permission recommendation*/}
            {activePermissionNotif && (
              <Layout.Section>
                <GrantPermissionsNotif
                  permissionURL={permissionURL}
                  valueRedirect="/manual-recommendation"
                />
              </Layout.Section>
            )}

            {/* Show banner sync status*/}
            {/* {!activePermissionNotif && (
              <Layout.Section>
                <BannerNotify
                  title={i18n.translate('AdminHTML.Recommendation.SyncBanner.bannerTitle')}
                  contents={[
                    i18n.translate('AdminHTML.Recommendation.SyncBanner.paragraph', {
                      t1: (
                        <strong>
                          {i18n.translate('AdminHTML.Recommendation.SyncBanner.paragraph_t1')}
                        </strong>
                      )
                    })
                  ]}
                  dismissName={'introduceRecommendationWidgetPage'}
                />
              </Layout.Section>
            )} */}

            {/* List of recommendation */}
            <Layout.Section>
              <div className="BC-Page__ManualType">
                <Card>
                  <Card.Section>
                    <Search
                      value={inputSearchText}
                      onClear={this.handleClearSearchFilter}
                      onChange={this.handleFiltersQueryChange}
                      handleKeyDown={this.handleKeyDown}
                      handleKeyUp={this.handleKeyUp}
                      // detect tab change to focus input -> fix bug when losing focus after typing and tab changes
                      selectedTab={selectedTab}
                    />
                  </Card.Section>

                  <Card.Section>
                    <ManualRecommendationType
                      inputSearchText={inputSearchText}
                      selectedTab={selectedTab}
                      onTabChange={this.handleTabChange}
                      onDelete={this.handleDeleteRecommendation}
                    />
                  </Card.Section>
                </Card>
              </div>
            </Layout.Section>
          </Layout>

          <div className="pagination">
            {isShowPagination && (
              <Pagination
                label={this.getPage()}
                hasPrevious={this.isAvailablePreviousPage()}
                onPrevious={this.getPreviousPage}
                hasNext={this.isAvailableNextPage()}
                onNext={this.getNextPage}
              />
            )}

            <div className="BC-Page BC-Page__Button">
              <Button
                primary
                disabled={isDisablePrimaryAction}
                onClick={() => {
                  history.push(routes.manualRecommendation.new());
                }}>
                {i18n.translate(
                  'AdminHTML.Recommendation.ManualRecommendation.Common.button_AddNew'
                )}
              </Button>
            </div>
          </div>
        </Page>
      </div>
    );
  }

  getTabIndex() {
    const { inputSearchText } = this.state;
    let handPickedProductTabIndex = HAND_PICKED_PRODUCT_TAB;
    let frequentlyBoughtTogetherTabIndex = FREQUENTLY_BOUGHT_TOGETHER_TAB;
    let relatedItemTabIndex = RELATED_ITEMS_TAB;

    if (!_.isEmpty(inputSearchText)) {
      handPickedProductTabIndex += 1;
      frequentlyBoughtTogetherTabIndex += 1;
      relatedItemTabIndex += 1;
    }

    return {
      handPickedProductTabIndex,
      frequentlyBoughtTogetherTabIndex,
      relatedItemTabIndex
    };
  }

  fetchManualRecommendation = (pageParam = 0, recommendationTypeParam = '') => {
    const { getManualRecommendations } = this.props;
    const { limit, selectedTab } = this.state;
    const { handPickedProductTabIndex, frequentlyBoughtTogetherTabIndex, relatedItemTabIndex } =
      this.getTabIndex();
    const page = this.getPage(recommendationTypeParam);

    let recommendationType = recommendationTypeParam;
    const mapRecommendationType = {
      [handPickedProductTabIndex]: MANUAL_RECOMMENDATION_TAB_TYPE.HAND_PICKED_PRODUCTS,
      [frequentlyBoughtTogetherTabIndex]: MANUAL_RECOMMENDATION_TAB_TYPE.FREQUENTLY_BOUGHT_TOGETHER,
      [relatedItemTabIndex]: MANUAL_RECOMMENDATION_TAB_TYPE.RELATED_ITEMS
    };
    if (!recommendationType) {
      recommendationType = mapRecommendationType[selectedTab];
    }

    if (pageParam) {
      this.updatePageFromNewPage(pageParam, recommendationType);
    }

    getManualRecommendations(
      {
        limit,
        skip: pageParam || page,
        recommendationType: recommendationType
      },
      recommendationType
    );
  };

  handleDeleteRecommendation = async recommendationId => {
    const {
      deleteManualRecommendation,
      removeRecommendationFromSearchResult,
      manualHandPickedRecommendations,
      manualRecommendationSearchResult
    } = this.props;
    const { inputSearchText, selectedTab } = this.state;

    await deleteManualRecommendation(recommendationId);

    // If delete recommendation in search result tab -> filter array result in redux
    if (inputSearchText && selectedTab === SEARCH_RESULT_MANUAL_RECOMMENDATION_TAB) {
      const manualRecommendation = manualRecommendationSearchResult.find(
        item => item.id === recommendationId
      );
      removeRecommendationFromSearchResult(recommendationId);

      if (manualRecommendation) {
        // Reset to page 1 whenever delete recommendation on tab search result
        this.fetchManualRecommendation(1, manualRecommendation.recommendationType);
      }
    } else {
      // If not in search result tab -> refresh data
      const { handPickedProductTabIndex } = this.getTabIndex();
      const page = this.getPage();

      let newPage = undefined;
      switch (selectedTab) {
        case handPickedProductTabIndex: {
          // If delete the last item on that page -> move to previous page
          if (manualHandPickedRecommendations.length === 1) {
            newPage = page > 1 ? page - 1 : page;
          }
          break;
        }
        default:
          break;
      }
      this.fetchManualRecommendation(newPage);
    }
  };

  // Search filter
  handleFiltersQueryChange = inputSearchText => {
    const { clearSearchManualRecommendation } = this.props;
    const { selectedTab } = this.state;
    const isHaveText = !_.isEmpty(inputSearchText);
    const newSelectedTab = selectedTab > 0 ? selectedTab - 1 : 0; // When empty text

    this.setState({
      inputSearchText,
      selectedTab: isHaveText ? SEARCH_RESULT_MANUAL_RECOMMENDATION_TAB : newSelectedTab
    });

    if (!isHaveText) {
      clearSearchManualRecommendation();
    }
  };

  // Clear filter
  handleClearSearchFilter = () => {
    const { clearSearchManualRecommendation } = this.props;
    const { selectedTab } = this.state;
    const newSelectedTab = selectedTab > 0 ? selectedTab - 1 : 0;

    this.setState({
      inputSearchText: '',
      selectedTab: newSelectedTab
    });
    clearSearchManualRecommendation();
  };

  // Key press event on search field
  handleKeyDown = event => {
    clearTimeout(this.timerTyping);
  };

  // Key up event, start searching after 500ms
  handleKeyUp = event => {
    // const { updateSearchManualRecommendationStatus } = this.props;
    // const charList = 'abcdefghijklmnopqrstuvwxyz0123456789';
    // const key = event.key.toLowerCase();
    // const keyCode = event.keyCode;

    // if (charList.indexOf(key) === -1 && keyCode !== 8) {
    //   updateSearchManualRecommendationStatus(true);
    //   return;
    // }

    // Invoke search request after 500ms
    this.timerTyping = setTimeout(() => {
      this.handleSearch();
    }, 500);
  };

  // Handle search filter action
  handleSearch = () => {
    const { searchManualRecommendations } = this.props;
    let inputSearchText = this.state.inputSearchText.toLowerCase().trim();
    inputSearchText = prettyText(inputSearchText);

    if (inputSearchText) {
      // TODO: 5000 should be adjust later if it's needed
      searchManualRecommendations({ q: encodeURIComponent(inputSearchText), limit: 5000 });
    }
  };

  // Handle change tab
  handleTabChange = selectedTabIndex => {
    this.setState({ selectedTab: selectedTabIndex });
  };

  // Check if have previous page
  isAvailablePreviousPage = () => {
    const { selectedTab, pageHandPicked } = this.state;
    const { handPickedProductTabIndex } = this.getTabIndex();

    switch (selectedTab) {
      case handPickedProductTabIndex:
        return pageHandPicked !== 1;
      default:
        return false;
    }
  };
  // Handle previous page
  getPreviousPage = () => {
    const page = this.getPage();
    const newPage = page - 1;
    this.fetchManualRecommendation(newPage);
  };

  // Check if have next page
  isAvailableNextPage = () => {
    const { selectedTab, pageHandPicked, totalPageHandPicked } = this.state;
    const { handPickedProductTabIndex } = this.getTabIndex();

    switch (selectedTab) {
      case handPickedProductTabIndex:
        return pageHandPicked < totalPageHandPicked;
      default:
        return false;
    }
  };
  // Handle next page
  getNextPage = () => {
    const page = this.getPage();
    const newPage = page + 1;
    this.fetchManualRecommendation(newPage);
  };

  // Get label to display in pagination component
  getPage = (recommendationTypeParam = '') => {
    const { selectedTab, pageHandPicked } = this.state;

    if (recommendationTypeParam) {
      const mapPage = {
        [MANUAL_RECOMMENDATION_TAB_TYPE.HAND_PICKED_PRODUCTS]: pageHandPicked
      };
      return mapPage[recommendationTypeParam] ? mapPage[recommendationTypeParam] : 1;
    }

    const { handPickedProductTabIndex } = this.getTabIndex();
    const mapPageByTab = {
      [handPickedProductTabIndex]: pageHandPicked
    };

    return mapPageByTab[selectedTab] ? mapPageByTab[selectedTab] : 1;
  };

  // Update page from new page depended on manual recommendation type
  updatePageFromNewPage = (newPage, recommendationTypeParam) => {
    const mapPageType = {
      [MANUAL_RECOMMENDATION_TAB_TYPE.HAND_PICKED_PRODUCTS]: 'pageHandPicked'
    };
    const pageType = mapPageType[recommendationTypeParam];

    this.setState({
      [pageType]: newPage
    });
  };

  // Show notification
  showGrantPermissionsModalNotif = () => {
    const { accessScopes } = this.props;

    // Remove localStorage
    try {
      localStorage.removeItem(`${getShop()}-permissionApproval-redirectURI`);
    } catch (error) {}

    // Check permission from access scopes' tenant app
    if (hasPermissionRecommendation(accessScopes)) return;

    // Get update permission link
    const permissionURL = buildUpdateAppPermissionURL(getShop());

    this.setState({ activePermissionNotif: true, permissionURL });
  };
}

const mapDispatchToProps = (dispatch, ownProps) => ({
  // Get manual recommendations
  getManualRecommendations: (queries = {}, type) =>
    dispatch(recommendationAction.getManualRecommendations(queries, type, ownProps.i18n)),

  // Search manual recommendations
  searchManualRecommendations: (queries = {}) =>
    dispatch(recommendationAction.searchManualRecommendations(queries, ownProps.i18n)),

  // Delete manual recommendation
  deleteManualRecommendation: recommendationId =>
    dispatch(recommendationAction.deleteManualRecommendation(recommendationId, ownProps.i18n)),

  // Clear manual recommendations search result
  clearSearchManualRecommendation: () =>
    dispatch(recommendationAction.clearSearchManualRecommendation()),

  // Remove manual recommendations from search result
  removeRecommendationFromSearchResult: recommendationId =>
    dispatch(recommendationAction.removeRecommendationFromSearchResult(recommendationId)),

  // Update search manual recommendations status
  updateSearchManualRecommendationStatus: status =>
    dispatch(recommendationAction.updateSearchManualRecommendationStatus(status))
});

const mapStateToProps = (state, props) => {
  // Get access scopes
  const accessScopes = _.get(state.oauth, 'tenantApp.accessScopes', {});
  const host = _.get(state.oauth, 'host', '');
  const settings = _.get(state.oauth, 'tenantApp.settings', {});

  // Get manual recommendations (type hand-picked-products)
  const manualHandPickedRecommendations = _.get(
    state.recommendation,
    'manualHandPickedRecommendations',
    []
  );
  const manualHandPickedTotal = _.get(state.recommendation, 'manualHandPickedTotal', 0);

  // Get manual recommendations search result
  const manualRecommendationSearchResult = _.get(
    state.recommendation,
    'manualRecommendationSearchResult',
    []
  );
  return {
    host,
    accessScopes,
    settings,
    manualHandPickedTotal,
    manualHandPickedRecommendations,
    manualRecommendationSearchResult
  };
};

export default withI18n()(
  connect(mapStateToProps, mapDispatchToProps)(ManualRecommendationManagement)
);
